import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Boxes,
  ClipboardCheck,
  CloudUpload,
  Megaphone,
  Menu,
  Search,
  Server,
  Sparkles,
  ShieldAlert,
  Users,
  X,
} from 'lucide-react';
import { Logo } from '../Logo';
import { UserMenu } from '../UserMenu';
import { useIsMobile } from '../../hooks/useIsMobile';
import { paths } from '../../routes/paths';

export type AdminToolId =
  | 'users'
  | 'campaigns'
  | 'taskTypes'
  | 'googleNexus'
  | 'llmTest'
  | 'tokenDashboard'
  | 'aiDetectionDashboard'
  | 'harborRunners';
export type AdminNavId = AdminToolId | 'browseTasks';

interface AdminToolDef {
  id: AdminToolId;
  slug: string;
  label: string;
  icon: React.ReactNode;
}

export const ADMIN_TOOLS: AdminToolDef[] = [
  { id: 'users', slug: 'users', label: 'Users', icon: <Users className="h-5 w-5" /> },
  { id: 'campaigns', slug: 'campaigns', label: 'Campaigns', icon: <Megaphone className="h-5 w-5" /> },
  { id: 'taskTypes', slug: 'task-types', label: 'Task Types', icon: <Boxes className="h-5 w-5" /> },
  { id: 'googleNexus', slug: 'google-nexus', label: 'Google Nexus', icon: <CloudUpload className="h-5 w-5" /> },
  { id: 'llmTest', slug: 'llm-test', label: 'LLM Test', icon: <Sparkles className="h-5 w-5" /> },
  { id: 'tokenDashboard', slug: 'token-dashboard', label: 'Token Dashboard', icon: <BarChart3 className="h-5 w-5" /> },
  { id: 'aiDetectionDashboard', slug: 'ai-detection', label: 'AI Detection', icon: <ShieldAlert className="h-5 w-5" /> },
  { id: 'harborRunners', slug: 'harbor-runners', label: 'Harbor Runners', icon: <Server className="h-5 w-5" /> },
];

export const DEFAULT_ADMIN_TOOL_ID: AdminToolId = 'users';

export function getAdminToolSlug(id: AdminToolId): string {
  return ADMIN_TOOLS.find((t) => t.id === id)?.slug ?? id;
}

export function getAdminToolIdFromSlug(slug: string): AdminToolId | null {
  return ADMIN_TOOLS.find((t) => t.slug === slug)?.id ?? null;
}

export function adminToolPath(id: AdminToolId): string {
  return paths.adminTool(getAdminToolSlug(id));
}

/**
 * Render-prop signature: AdminLayout passes a `mobileNavTrigger` element back
 * to the page so it can position the hamburger inside its own page heading
 * row. On desktop the trigger is `null`.
 */
export type AdminLayoutContentRenderer = (ctx: {
  mobileNavTrigger: React.ReactNode;
}) => React.ReactNode;

interface AdminLayoutProps {
  /** Page content. Use the function form to receive `mobileNavTrigger`. */
  children: React.ReactNode | AdminLayoutContentRenderer;
  /** Which sidebar item is highlighted. */
  activeId: AdminNavId;
  /**
   * Called when the user clicks a sidebar tool. The host page typically
   * navigates to `/admin/<slug>` via `adminToolPath(id)`.
   */
  onSelectTool: (id: AdminToolId) => void;
  /**
   * Called when the user clicks "Browse Tasks". Defaults to navigating to
   * `/admin/browse-tasks`.
   */
  onSelectBrowseTasks?: () => void;
  /** Header back-button label and target. */
  backLabel: string;
  onBack: () => void;
  /** Header pill text — defaults to "Admin". */
  badge?: string;
}

export function AdminLayout({
  children,
  activeId,
  onSelectTool,
  onSelectBrowseTasks,
  backLabel,
  onBack,
  badge = 'Admin',
}: AdminLayoutProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [navOpen, setNavOpen] = useState(false);

  const browseTasks = onSelectBrowseTasks ?? (() => navigate(paths.browseTasks()));
  const renderContent = (mobileNavTrigger: React.ReactNode) =>
    typeof children === 'function'
      ? (children as AdminLayoutContentRenderer)({ mobileNavTrigger })
      : children;

  if (isMobile) {
    const mobileNavTrigger = (
      <button
        type="button"
        aria-label="Open admin tools"
        onClick={() => setNavOpen(true)}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900"
      >
        <Menu className="h-5 w-5" />
      </button>
    );

    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <AdminHeader badge={badge} backLabel={backLabel} onBack={onBack} compact />
        <main className="min-w-0 flex-1 px-4 py-5">{renderContent(mobileNavTrigger)}</main>
        <AdminNavSheet
          open={navOpen}
          activeId={activeId}
          onClose={() => setNavOpen(false)}
          onSelect={(id) => {
            setNavOpen(false);
            onSelectTool(id);
          }}
          onBrowseTasks={() => {
            setNavOpen(false);
            browseTasks();
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AdminHeader badge={badge} backLabel={backLabel} onBack={onBack} />
      <div className="flex-1 flex">
        <aside className="w-60 bg-white border-r border-gray-200">
          <nav className="p-3 space-y-1">
            {ADMIN_TOOLS.map((tool) => {
              const active = activeId === tool.id;
              return (
                <button
                  key={tool.id}
                  type="button"
                  onClick={() => onSelectTool(tool.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    active ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {tool.icon}
                  {tool.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={browseTasks}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeId === 'browseTasks'
                  ? 'bg-primary/10 text-primary'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Search className="h-5 w-5" />
              Browse Tasks
            </button>
          </nav>
        </aside>
        <main className="min-w-0 flex-1 p-8">{renderContent(null)}</main>
      </div>
    </div>
  );
}

interface AdminHeaderProps {
  badge: string;
  backLabel: string;
  onBack: () => void;
  compact?: boolean;
}

export function AdminHeader({ badge, backLabel, onBack, compact = false }: AdminHeaderProps) {
  return (
    <header className="bg-white border-b border-gray-200">
      <div
        className={`${compact ? 'px-4 py-3' : 'max-w-full px-4 sm:px-6 lg:px-8 py-4'} flex items-center justify-between gap-3`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <ClipboardCheck className="h-7 w-7 shrink-0 text-primary" />
          <Logo className="truncate text-xl font-bold text-gray-900" />
          <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
            {badge}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className={compact ? 'sr-only' : undefined}>{backLabel}</span>
          </button>
          <UserMenu />
        </div>
      </div>
    </header>
  );
}

interface AdminNavSheetProps {
  open: boolean;
  activeId: AdminNavId;
  onClose: () => void;
  onSelect: (id: AdminToolId) => void;
  onBrowseTasks: () => void;
}

export function AdminNavSheet({
  open,
  activeId,
  onClose,
  onSelect,
  onBrowseTasks,
}: AdminNavSheetProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="Admin tools">
      <button
        type="button"
        aria-label="Close admin tools"
        onClick={onClose}
        className="absolute inset-0 h-full w-full bg-gray-900/40"
      />
      <div className="absolute inset-x-0 bottom-0 rounded-t-md bg-white p-4 shadow-xl">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Admin Tools</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="space-y-2">
          {ADMIN_TOOLS.map((tool) => {
            const active = tool.id === activeId;
            return (
              <button
                key={tool.id}
                type="button"
                onClick={() => onSelect(tool.id)}
                className={`flex min-h-[46px] w-full items-center gap-3 rounded-md px-3 text-sm font-medium ${
                  active ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {tool.icon}
                {tool.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={onBrowseTasks}
            className={`flex min-h-[46px] w-full items-center gap-3 rounded-md px-3 text-sm font-medium ${
              activeId === 'browseTasks'
                ? 'bg-primary/10 text-primary'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Search className="h-5 w-5" />
            Browse Tasks
          </button>
        </nav>
      </div>
    </div>
  );
}
